import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import Toast from "../components/Toast";

interface ShowToastOptions {
    type?: string;
    message?: string;
    autohide?: boolean;
    duration?: number;
    showCloseButton?: boolean;
    fullWidth?: boolean;
    maxWidth?: string | number;
    minWidth?: string | number;
    onClose?: () => void;
}

interface ToastItem extends Required<Pick<ShowToastOptions, 'type' | 'message' | 'autohide' | 'fullWidth'>> {
    id: number;
    duration?: number;
    showCloseButton?: boolean;
    maxWidth?: string | number;
    minWidth?: string | number;
    onClose?: () => void;
}

export interface ToastContextValue {
    showToast: (options?: ShowToastOptions) => number | null;
    showInformationToast: (message: string, options?: Omit<ShowToastOptions, 'type' | 'message'>) => number | null;
    showSuccessToast: (message: string, options?: Omit<ShowToastOptions, 'type' | 'message'>) => number | null;
    showErrorToast: (defaultMessage: string, error?: unknown, options?: Omit<ShowToastOptions, 'type' | 'message'>) => number | null;
    dismissToast: (id: number) => void;
    clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function formatErrorMessage(defaultMessage: string, error?: unknown): string {
    const hasDefault = typeof defaultMessage === "string" && defaultMessage.trim();
    const baseMessage = hasDefault ? defaultMessage.trim() : "เกิดข้อผิดพลาด";
    const detail = (error as { detail?: string })?.detail ? `: ${(error as { detail: string }).detail}` : "";
    return `${baseMessage}${detail}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    const showToast = useCallback(({ type = "information", message = "", autohide = true, duration, showCloseButton, fullWidth = false, maxWidth, minWidth, onClose }: ShowToastOptions = {}): number | null => {
        const normalizedMessage = typeof message === "string" ? message.trim() : "";

        if (!normalizedMessage) {
            console.warn("Toast message is required");
            return null;
        }

        const id = toastIdRef.current++;

        setToasts((prev) => [
            ...prev,
            {
                id,
                type,
                message: normalizedMessage,
                autohide,
                duration,
                showCloseButton,
                fullWidth,
                maxWidth,
                minWidth,
                onClose,
            },
        ]);

        return id;
    }, []);

    const showInformationToast = useCallback((message: string, options: Omit<ShowToastOptions, 'type' | 'message'> = {}): number | null => {
        return showToast({ ...options, type: "information", message });
    }, [showToast]);

    const showSuccessToast = useCallback((message: string, options: Omit<ShowToastOptions, 'type' | 'message'> = {}): number | null => {
        return showToast({ ...options, type: "success", message });
    }, [showToast]);

    const showErrorToast = useCallback((defaultMessage: string, error?: unknown, options: Omit<ShowToastOptions, 'type' | 'message'> = {}): number | null => {
        const message = formatErrorMessage(defaultMessage, error);
        return showToast({ ...options, type: "error", message });
    }, [showToast]);

    const handleToastClose = useCallback((id: number): void => {
        let closedToast: ToastItem | null = null;

        setToasts((prev) => {
            closedToast = prev.find((toast) => toast.id === id) || null;
            if (!closedToast) {
                return prev;
            }
            return prev.filter((toast) => toast.id !== id);
        });

        if (closedToast?.onClose) {
            closedToast.onClose();
        }
    }, []);

    const dismissToast = useCallback((id: number): void => {
        handleToastClose(id);
    }, [handleToastClose]);

    const clearToasts = useCallback((): void => {
        let activeToasts: ToastItem[] = [];
        setToasts((prev) => {
            activeToasts = prev;
            return [];
        });

        activeToasts.forEach((toast) => {
            toast.onClose?.();
        });
    }, []);

    const contextValue = useMemo<ToastContextValue>(() => ({
        showToast,
        showInformationToast,
        showSuccessToast,
        showErrorToast,
        dismissToast,
        clearToasts,
    }), [showToast, showInformationToast, showSuccessToast, showErrorToast, dismissToast, clearToasts]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {toasts.length > 0 && (
                <div
                    className="pointer-events-none fixed inset-x-0 bottom-0 z-[2000] flex flex-col gap-3 p-6"
                    aria-live="assertive"
                    aria-atomic="true"
                >
                    {toasts.map((toast) => (
                        <div key={toast.id} className={`pointer-events-auto drop-shadow-lg ${toast.fullWidth ? "w-full" : "w-fit self-end"}`}>
                            <Toast
                                type={toast.type}
                                message={toast.message}
                                autohide={toast.autohide}
                                duration={toast.duration}
                                showCloseButton={toast.showCloseButton}
                                fullWidth={toast.fullWidth}
                                maxWidth={toast.maxWidth}
                                minWidth={toast.minWidth}
                                onClose={() => handleToastClose(toast.id)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
}
